import React, { useContext, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSocketEvent } from '../context/SocketContext';
import { useConversationContext } from '../context/ConversationContext';
import { useArchiveContext } from '../context/ArchiveContext';
import { NotificationContext } from '../context/NotificationContext';
import { AppContext } from '../window';
import { SocketEvent, ConversationUpdate } from '../models/Socket';
import { Conversation } from '../models/Conversation';
import { fetchMessagesByConversationId } from '../api/chatApi';

const ChatSocketManager: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const appContext = useContext(AppContext);
  const { addNotification } = useContext(NotificationContext);
  const { onConversationUpdate, conversations, handleConversationClick } = useConversationContext();
  const { archivedConversations, refreshArchivedConversations } = useArchiveContext();

  // Helper to check if user is currently on chat/inbox page
  const isOnChatPage = useCallback(() => {
    return location.pathname.startsWith('/inbox');
  }, [location.pathname]);

  // Unified notification click handler that properly resets conversation count
  const handleNotificationClick = useCallback((conversation: Conversation) => {
    if (conversation) {
      handleConversationClick(conversation, navigate, appContext.setUser);
    }
  }, [handleConversationClick, navigate, appContext.setUser]);

  // Handle conversation update events
  const handleConversationUpdate = useCallback(async (event: SocketEvent<ConversationUpdate>) => {
    const { conversationId, messageId } = event.data;
    
    try {
      // Check if conversation exists in inbox
      const existingConversation = conversations.find(c => c.id === conversationId);
      
      // Check if conversation is archived
      const isArchived = archivedConversations.some(c => c.id === conversationId);
      
      if (isArchived) {
        // If it's archived, refresh the archived conversations
        refreshArchivedConversations();
        return;
      }

      // Fetch the new message
      const { data: messages } = await fetchMessagesByConversationId(conversationId, 1);
      const newMessage = messages.find(m => m.id === messageId) || messages[0];
      if (!newMessage) return;

      let updatedConversation: Conversation | null = null;
      
      if (existingConversation) {
        // Update existing conversation
        updatedConversation = await onConversationUpdate({
          type: 'message_added',
          conversationId,
          message: newMessage
        });
      } else {
        // Add new conversation to inbox
        updatedConversation = await onConversationUpdate({
          type: 'new_conversation',
          conversationId,
          message: newMessage
        });
      }

      // Update unread message count in app context
      if (newMessage.fromUserId !== appContext.user?.id) {
        appContext.setUser(prevUser => 
          prevUser ? {
            ...prevUser,
            unreadMessageCount: +prevUser.unreadMessageCount + 1
          } : prevUser
        );
      }
      // Show notification if not on chat page and message is from another user
      if (newMessage.fromUserId !== appContext.user?.id && updatedConversation) {
        
        // Play sound if enabled
        if (appContext.user?.credentials?.soundStatus === 1) {
          const audio = new Audio('/data/nm_v1.mp3');
          audio.play().catch(console.error);
        }

        if(!isOnChatPage()){
          const recipientName = updatedConversation.recipient?.nickname || 'Someone';
          addNotification({
            message: `New message from ${recipientName}`,
            level: 'info',
            timeout: 60000,
            dismissible: true,
            avatarUrl: updatedConversation.recipient?.displayPicture?.thumb,
            onClick: () => handleNotificationClick(updatedConversation)
          });

          // Also show browser notification if permissions are granted
          if ('Notification' in window && Notification.permission === 'granted') {
            const notification = new Notification(`New message from ${recipientName}`, {
              body: newMessage.content || 'Sent a message',
              icon: updatedConversation.recipient?.displayPicture?.thumb || '/default-avatar.png',
              tag: `conversation-${conversationId}`
            });

            notification.onclick = () => {
              window.focus();
              handleNotificationClick(updatedConversation);
              notification.close();
            };
          }
        }
      }
    } catch (error) {
      console.error('Failed to handle conversation update:', error);
    }
  }, [
    isOnChatPage, 
    onConversationUpdate, 
    conversations,
    archivedConversations, 
    refreshArchivedConversations, 
    addNotification, 
    appContext,
    navigate,
    handleNotificationClick
  ]);

  // Subscribe to conversation update events
  useSocketEvent<ConversationUpdate>('event/conversation-update', handleConversationUpdate);

  // This component doesn't render anything
  return null;
};

export default ChatSocketManager;